/**
 * API 服务 - 处理与后端的通信
 */

// 获取 API 基础 URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

/**
 * 调用推荐 API
 * @param {string} userId - 用户 ID
 * @param {string} instruction - 推荐指令（可选）
 * @returns {Promise<Object>} 推荐结果
 */
export async function runRecommendation(userId, instruction = 'default') {
  try {
    const response = await fetch(`${API_BASE_URL}/api/run_recommendation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        instruction: instruction,
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Recommendation API call failed:', error);
    throw error;
  }
}

/**
 * 调用记忆更新 API (Stage W)
 * @param {string} userId - 用户 ID
 * @param {string} itemId - 物品 ID
 * @returns {Promise<Object>} 更新结果
 */
export async function runMemoryUpdate(userId, itemId) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/run_memory_update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        item_id: itemId,
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Memory update API call failed:', error);
    throw error;
  }
}

/**
 * 加载本地 Mock 数据（开发测试用）
 * @param {string} userId - 用户 ID
 * @returns {Promise<Object>} Mock 推荐结果
 */
export async function loadMockRecommendation(userId) {
  try {
    // 模拟网络延迟
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // 加载对应的 case study 数据
    // 使用 Vite 的 BASE_URL 确保路径正确
    const baseUrl = import.meta.env.BASE_URL || '/demo/';
    const response = await fetch(`${baseUrl}case_study/top5_user_${userId}_case_study.json`);
    
    if (!response.ok) {
      throw new Error(`Test data not found for user ${userId}`);
    }
    
    const data = await response.json();
    
    // 转换为前端需要的格式
    return transformCaseStudyData(data);
  } catch (error) {
    console.error('Failed to load mock data:', error);
    throw error;
  }
}

/**
 * 加载本地 Mock 记忆更新数据（开发测试用）
 * @param {string} userId - 用户 ID
 * @param {string} itemId - 物品 ID
 * @returns {Promise<Object>} Mock 更新结果
 */
export async function loadMockMemoryUpdate(userId, itemId) {
  try {
    // 模拟网络延迟
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // 加载对应的 case study 数据
    // 使用 Vite 的 BASE_URL 确保路径正确
    const baseUrl = import.meta.env.BASE_URL || '/demo/';
    const response = await fetch(`${baseUrl}case_study/top5_user_${userId}_case_study.json`);
    
    if (!response.ok) {
      throw new Error(`Test data not found for user ${userId}`);
    }
    
    const data = await response.json();
    
    // 转换为记忆更新格式
    return transformMemoryUpdateData(data);
  } catch (error) {
    console.error('Failed to load mock memory update data:', error);
    throw error;
  }
}

/**
 * 转换 case study 数据为前端格式
 */
function transformCaseStudyData(caseStudy) {
  // 解析邻居数据
  const stageRPrompt = caseStudy.stage_r_conv?.messages?.[0]?.content || 
                       caseStudy.stage_r_conversation?.messages?.[0]?.content || '';
  const neighbors = parseNeighborsFromPrompt(stageRPrompt);
  
  // 提取 LLM 原始评分（从 stage_rr_info）
  let llmScore = caseStudy.score; // 默认使用综合分数
  let llmRationale = caseStudy.rationale;
  
  if (caseStudy.stage_rr_info?.scores) {
    // 查找当前推荐物品的 LLM 评分
    const scoreInfo = caseStudy.stage_rr_info.scores.find(
      s => s.item_id === caseStudy.item_id
    );
    
    if (scoreInfo) {
      llmScore = scoreInfo.score; // 使用 LLM 原始评分（0-1）
      llmRationale = scoreInfo.rationale || caseStudy.rationale;
    }
  }
  
  // 提取 rerank 列表（按分数排序）
  let rerankScores = [];
  if (caseStudy.stage_rr_info?.scores) {
    rerankScores = caseStudy.stage_rr_info.scores
      .map(s => ({
        item_id: s.item_id,
        score: s.score,
        rationale: s.rationale,
        is_recommended: s.item_id === caseStudy.item_id
      }))
      .sort((a, b) => b.score - a.score); // 按分数降序排列
  }
  
  return {
    user_id: caseStudy.user_id,
    item_id: caseStudy.item_id,
    item_title: caseStudy.item_title,
    user_history: caseStudy.user_history,
    rationale: llmRationale, // 使用 LLM 的 rationale
    score: llmScore, // 使用 LLM 原始评分
    
    // 保留综合分数供参考
    composite_score: caseStudy.score,
    
    // Stage R - 原始邻居数据
    raw_neighbors: neighbors,
    
    // Stage R - 合成的偏好 facets
    synthesized_facets: caseStudy.stage_r_parsed?.facets || 
                       caseStudy.stage_r_info?.facets || [],
    
    // Stage R - 详细信息
    stage_r_details: caseStudy.details?.stage_r,
    
    // Stage ReRank - 完整排序列表
    rerank_scores: rerankScores,
    
    // Stage W - 记忆更新信息（用于后续展示）
    stage_w_parsed: caseStudy.stage_w_parsed || caseStudy.stage_w_info,
    stage_w_conversation: caseStudy.stage_w_conversation || caseStudy.stage_w_conv,
  };
}

/**
 * 从 prompt 中解析邻居数据
 */
function parseNeighborsFromPrompt(prompt) {
  const neighbors = {
    item_neighbors: [],
    user_neighbors: [],
  };
  
  // 提取 item 邻居
  // 使用更宽松的正则表达式，匹配 [Item-XXX] ... (score=XXX) 格式
  const itemPattern = /\[Item-(\d+)\]\s*([^\n]+?)\s*\(score=([\d.]+)\)/g;
  let match;
  
  while ((match = itemPattern.exec(prompt)) !== null) {
    const fullText = match[2].trim();
    // 尝试分离标题和描述（如果有 | 分隔符）
    const parts = fullText.split('|');
    const title = parts[0].trim();
    const description = parts.length > 1 ? parts.slice(1).join('|').trim() : '';
    
    neighbors.item_neighbors.push({
      id: match[1],
      title: title,
      description: description,
      score: parseFloat(match[3]),
    });
  }
  
  // 提取 user 邻居
  const userPattern = /\[User-(\d+)\]\s*\(overlap_score=([\d.]+)\)\s*-\s*Recent:\s*([^\n]+)/g;
  
  while ((match = userPattern.exec(prompt)) !== null) {
    neighbors.user_neighbors.push({
      id: match[1],
      overlap_score: parseFloat(match[2]),
      recent_items: match[3].trim(),
    });
  }
  
  return neighbors;
}

/**
 * 转换记忆更新数据为前端格式
 */
function transformMemoryUpdateData(caseStudy) {
  // 兼容新旧字段名
  const stageWConv = caseStudy.stage_w_conv || caseStudy.stage_w_conversation;
  const stageWPrompt = stageWConv?.messages?.[0]?.content || '';
  const stageWResponse = stageWConv?.response || '';
  
  // 解析响应中的 JSON
  let parsedResponse;
  try {
    parsedResponse = JSON.parse(stageWResponse);
  } catch (e) {
    console.error('Failed to parse Stage W response:', e);
    parsedResponse = caseStudy.stage_w_parsed || caseStudy.stage_w_info || {};
  }
  
  // 提取更新前的用户记忆（从 prompt）
  const userMemoryBeforeMatch = stageWPrompt.match(/\*\*Current Personal Memory of User \d+:\*\*\s*([^\*]+)/);
  const userMemoryBefore = userMemoryBeforeMatch ? userMemoryBeforeMatch[1].trim() : 'No previous memory recorded.';
  
  // 提取更新后的用户记忆
  const userMemoryAfter = parsedResponse.user_memory || 'No memory update available.';
  
  // 提取物品记忆更新
  const itemMemoryBeforeMatch = stageWPrompt.match(/\*\*Current Memory of Item \d+ \([^\)]+\):\*\*\s*([^\*]+)/);
  const itemMemoryBefore = itemMemoryBeforeMatch ? itemMemoryBeforeMatch[1].trim() : 'No previous item memory.';
  const itemMemoryAfter = parsedResponse.item_memory || 'No item memory update available.';
  
  // 提取所有邻居更新
  const neighborUpdates = parsedResponse.neighbor_updates || [];
  
  // 处理所有邻居的更新
  const allNeighborUpdates = neighborUpdates.map(neighbor => {
    let neighborMemoryBefore = '(No memory)';
    
    // 从 prompt 中查找该邻居的原始记忆
    const neighborPattern = new RegExp(
      `${neighbor.neighbor_id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*?Current memory:\\s*([^\\n]+(?:\\n(?!\\d+\\.).*)*?)(?=\\n\\d+\\.|\\n\\*|$)`,
      'i'
    );
    const neighborMatch = stageWPrompt.match(neighborPattern);
    if (neighborMatch) {
      neighborMemoryBefore = neighborMatch[1].trim();
    }
    
    return {
      neighbor_id: neighbor.neighbor_id,
      before: neighborMemoryBefore,
      after: neighbor.memory_update || 'No update.',
      rationale: neighbor.rationale || 'Memory propagation to collaborative neighbor.',
    };
  });
  
  // 保留第一个邻居作为主要展示（向后兼容）
  const primaryNeighbor = allNeighborUpdates.length > 0 ? allNeighborUpdates[0] : {
    neighbor_id: 'N/A',
    before: 'No neighbor selected for update.',
    after: 'No neighbor memory update.',
    rationale: 'No propagation data available.',
  };
  
  return {
    user_id: caseStudy.user_id,
    item_id: caseStudy.item_id,
    item_title: caseStudy.item_title,
    
    user_update: {
      before: userMemoryBefore,
      after: userMemoryAfter,
    },
    
    item_update: {
      before: itemMemoryBefore,
      after: itemMemoryAfter,
    },
    
    // 主要邻居更新（向后兼容）
    propagated_neighbor_update: primaryNeighbor,
    
    // 所有邻居更新
    all_neighbor_updates: allNeighborUpdates,
    total_neighbors_updated: neighborUpdates.length,
  };
}

